import { useCallback } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useMutation } from '@tanstack/react-query';

import { Spinner, showToast, Switch } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';
import { useWorkersService } from '@restworld/data-services';

import { FlexRow } from './account-settings-tab';
import { setWorkerAtom, workerAtom } from '../../global-state.worker-data';

const INIT = Symbol(); // or anything that doesn't conflict with data.
const showProposedJobApplicationsAtom = atom<boolean>(false); // this is hidden (not exported)
const feedWorkerDataAtom = atom(
  (get) => get(showProposedJobApplicationsAtom),
  (get, set, arg) => {
    if (arg === INIT) {
      const worker = get(workerAtom);
      set(showProposedJobApplicationsAtom, worker?.data?.proposal_notifications_enabled);
    } else {
      set(showProposedJobApplicationsAtom, arg);
    }
  }
);
feedWorkerDataAtom.onMount = (dispatch) => {
  dispatch(INIT);
};

export const ProposedJobPositionSetting = () => {
  const { mutate: onReceiveJobOffersInLineWithYourProfileByEmail, isPending } = useUpdateWorkerMutation();
  const { t } = useTranslation('user-settings');
  const showProposedJobApplications = useAtomValue(showProposedJobApplicationsAtom);

  const onToggleSwitch = useCallback(
    async (enabled: boolean) => onReceiveJobOffersInLineWithYourProfileByEmail(enabled),
    [onReceiveJobOffersInLineWithYourProfileByEmail]
  );
  useAtom(feedWorkerDataAtom);
  return (
    <FlexRow className="gap-8 justify-between items-start w-full">
      <span>{t('notification_tab_proposed_job_settings_header')}</span>
      <div className="inline-flex gap-2 items-center justify-end w-full max-w-[100px]">
        <Switch
          checked={showProposedJobApplications}
          onChange={onToggleSwitch}
          srOnlyLabel={t('notification_tab_switch_label')}
        />
        {isPending && <Spinner color="primary" size="sm" />}
      </div>
    </FlexRow>
  );
};
const useUpdateWorkerMutation = () => {
  const { t } = useTranslation('user-settings');

  const workerService = useWorkersService();
  const onSetWorkerData = useSetAtom(setWorkerAtom);
  const showProposedJobApplications = useSetAtom(showProposedJobApplicationsAtom);
  const { user } = useAuth();
  return useMutation({
    onMutate: (proposalNotificationsEnabled) => {
      showProposedJobApplications(proposalNotificationsEnabled);
    },
    mutationFn: async (proposalNotificationsEnabled: boolean) =>
      (
        await workerService.updateWorker({
          workerId: user?.id,
          params: {
            proposal_notifications_enabled: proposalNotificationsEnabled,
          },
        })
      ).data,
    onSuccess: (data) => {
      onSetWorkerData({ ...data });
    },
    onError: (error, proposalNotificationsEnabled) => {
      showToast(t('error_updating_proposal_notifications_status'), 'error', {
        autoClose: 3500,
        position: 'top-right',
      });
      showProposedJobApplications(!proposalNotificationsEnabled);
      console.error('Error updating worker data:', error);
    },
  });
};
